import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

// ============== TYPES ==============

export type NCRType = 'internal' | 'supplier' | 'customer';
export type NCRSeverity = 'critical' | 'major' | 'minor';
export type NCRStatus = 'open' | 'investigation' | 'corrective_action' | 'verification' | 'closed' | 'cancelled';

export interface NCR {
  id: string;
  ncr_number: string;
  title: string;
  description: string;
  ncr_type: NCRType;
  severity: NCRSeverity;
  category: string | null;
  status: NCRStatus;
  opened_at: string;
  investigation_started_at: string | null;
  corrective_action_started_at: string | null;
  verification_started_at: string | null;
  closed_at: string | null;
  due_date: string | null;
  reported_by: string;
  assigned_to: string | null;
  approved_by: string | null;
  closed_by: string | null;
  work_order_id: string | null;
  inspection_id: string | null;
  batch_id: string | null;
  machine_id: string | null;
  mold_id: string | null;
  customer_id: string | null;
  root_cause: string | null;
  root_cause_method: string | null;
  immediate_action: string | null;
  corrective_action: string | null;
  preventive_action: string | null;
  verification_method: string | null;
  verification_result: string | null;
  effectiveness_verified: boolean;
  estimated_cost: number | null;
  actual_cost: number | null;
  attachments: any[];
  tags: string[] | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface NCRDetailed extends NCR {
  reporter_name: string | null;
  assignee_name: string | null;
  approver_name: string | null;
  closer_name: string | null;
  work_order_number: string | null;
  work_order_product: string | null;
  inspection_number: string | null;
  batch_number: string | null;
  machine_name: string | null;
  mold_name: string | null;
  customer_name: string | null;
  days_open: number;
  is_overdue: boolean;
}

export interface NCRHistory {
  id: string;
  ncr_id: string;
  action: string;
  previous_status: NCRStatus | null;
  new_status: NCRStatus | null;
  comment: string | null;
  user_id: string | null;
  created_at: string;
  user_name?: string;
}

export interface NCRStats {
  total_ncrs: number;
  open_ncrs: number;
  investigation_ncrs: number;
  corrective_action_ncrs: number;
  verification_ncrs: number;
  closed_ncrs: number;
  cancelled_ncrs: number;
  critical_ncrs: number;
  major_ncrs: number;
  minor_ncrs: number;
  overdue_ncrs: number;
  avg_days_to_close: number;
}

export interface CreateNCRData {
  title: string;
  description: string;
  ncr_type?: NCRType;
  severity?: NCRSeverity;
  category?: string;
  due_date?: string;
  assigned_to?: string;
  work_order_id?: string;
  inspection_id?: string;
  batch_id?: string;
  machine_id?: string;
  mold_id?: string;
  customer_id?: string;
  immediate_action?: string;
  tags?: string[];
}

export interface UpdateNCRData extends Partial<CreateNCRData> {
  root_cause?: string;
  root_cause_method?: string;
  corrective_action?: string;
  preventive_action?: string;
  verification_method?: string;
  verification_result?: string;
  effectiveness_verified?: boolean;
  estimated_cost?: number;
  actual_cost?: number;
  notes?: string;
}

// ============== QUERIES ==============

export function useNCRList(filters?: {
  status?: NCRStatus | 'all';
  severity?: NCRSeverity | 'all';
  type?: NCRType | 'all';
}) {
  return useQuery({
    queryKey: ['ncrs', filters],
    queryFn: async () => {
      let query = api
        .from('ncr_detailed')
        .select('*')
        .order('opened_at', { ascending: false });

      if (filters?.status && filters.status !== 'all') {
        query = query.eq('status', filters.status);
      }
      if (filters?.severity && filters.severity !== 'all') {
        query = query.eq('severity', filters.severity);
      }
      if (filters?.type && filters.type !== 'all') {
        query = query.eq('ncr_type', filters.type);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as NCRDetailed[];
    },
  });
}

export function useNCR(id: string | null) {
  return useQuery({
    queryKey: ['ncr', id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await api
        .from('ncr_detailed')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      return data as NCRDetailed;
    },
    enabled: !!id,
  });
}

export function useNCRHistory(ncrId: string | null) {
  return useQuery({
    queryKey: ['ncr-history', ncrId],
    queryFn: async () => {
      if (!ncrId) return [];
      const { data, error } = await api
        .from('ncr_history')
        .select(`
          *,
          profiles:user_id(full_name)
        `)
        .eq('ncr_id', ncrId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return (data || []).map((h: any) => ({
        ...h,
        user_name: h.profiles?.full_name || 'Sistema',
      })) as NCRHistory[];
    },
    enabled: !!ncrId,
  });
}

export function useNCRStats() {
  return useQuery({
    queryKey: ['ncr-stats'],
    queryFn: async () => {
      const { data, error } = await api
        .from('ncr_stats')
        .select('*')
        .single();

      if (error) throw error;
      return data as NCRStats;
    },
  });
}

// ============== MUTATIONS ==============

export function useCreateNCR() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { userId } = useAuth();

  return useMutation({
    mutationFn: async (data: CreateNCRData) => {
      const { data: result, error } = await api
        .from('non_conformance_reports')
        .insert([{
          ...data,
          reported_by: userId,
        }])
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['ncrs'] });
      queryClient.invalidateQueries({ queryKey: ['ncr-stats'] });
      toast({
        title: 'NCR creada',
        description: `No conformidad ${data.ncr_number} registrada correctamente`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo crear la NCR',
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateNCR() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateNCRData }) => {
      const { data: result, error } = await api
        .from('non_conformance_reports')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ncrs'] });
      queryClient.invalidateQueries({ queryKey: ['ncr'] });
      queryClient.invalidateQueries({ queryKey: ['ncr-stats'] });
      toast({
        title: 'NCR actualizada',
        description: 'Los cambios se guardaron correctamente',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo actualizar la NCR',
        variant: 'destructive',
      });
    },
  });
}

export function useChangeNCRStatus() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { userId } = useAuth();

  return useMutation({
    mutationFn: async ({
      ncrId,
      newStatus,
      comment,
    }: {
      ncrId: string;
      newStatus: NCRStatus;
      comment?: string;
    }) => {
      const { error } = await api.rpc('change_ncr_status', {
        p_ncr_id: ncrId,
        p_new_status: newStatus,
        p_user_id: userId,
        p_comment: comment || null,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ncrs'] });
      queryClient.invalidateQueries({ queryKey: ['ncr'] });
      queryClient.invalidateQueries({ queryKey: ['ncr-history'] });
      queryClient.invalidateQueries({ queryKey: ['ncr-stats'] });
      toast({
        title: 'Estado actualizado',
        description: 'El estado de la NCR se cambió correctamente',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo cambiar el estado',
        variant: 'destructive',
      });
    },
  });
}

export function useAddNCRComment() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { userId } = useAuth();

  return useMutation({
    mutationFn: async ({ ncrId, comment }: { ncrId: string; comment: string }) => {
      const { error } = await api.from('ncr_history').insert([{
        ncr_id: ncrId,
        action: 'comment',
        comment,
        user_id: userId,
      }]);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ncr-history'] });
      toast({
        title: 'Comentario agregado',
        description: 'El comentario se registró correctamente',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo agregar el comentario',
        variant: 'destructive',
      });
    },
  });
}

// ============== HELPERS ==============

export const NCR_CATEGORIES = [
  'Material defectuoso',
  'Error de proceso',
  'Fallo de equipo',
  'Error de documentación',
  'Incumplimiento de especificación',
  'Contaminación',
  'Daño en transporte',
  'Error humano',
  'Fallo de proveedor',
  'Otro',
];

export const ROOT_CAUSE_METHODS = [
  { value: '5why', label: '5 Por qué' },
  { value: 'fishbone', label: 'Diagrama Ishikawa' },
  { value: 'fmea', label: 'FMEA' },
  { value: 'pareto', label: 'Análisis Pareto' },
  { value: 'other', label: 'Otro' },
];

export function getStatusLabel(status: NCRStatus): string {
  const labels: Record<NCRStatus, string> = {
    open: 'Abierta',
    investigation: 'En Investigación',
    corrective_action: 'Acción Correctiva',
    verification: 'Verificación',
    closed: 'Cerrada',
    cancelled: 'Cancelada',
  };
  return labels[status] || status;
}

export function getStatusColor(status: NCRStatus): string {
  const colors: Record<NCRStatus, string> = {
    open: 'bg-red-100 text-red-700 border-red-200',
    investigation: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    corrective_action: 'bg-blue-100 text-blue-700 border-blue-200',
    verification: 'bg-purple-100 text-purple-700 border-purple-200',
    closed: 'bg-green-100 text-green-700 border-green-200',
    cancelled: 'bg-gray-100 text-gray-700 border-gray-200',
  };
  return colors[status] || 'bg-gray-100 text-gray-700';
}

export function getSeverityLabel(severity: NCRSeverity): string {
  const labels: Record<NCRSeverity, string> = {
    critical: 'Crítica',
    major: 'Mayor',
    minor: 'Menor',
  };
  return labels[severity] || severity;
}

export function getSeverityColor(severity: NCRSeverity): string {
  const colors: Record<NCRSeverity, string> = {
    critical: 'bg-red-500 text-white',
    major: 'bg-orange-500 text-white',
    minor: 'bg-yellow-500 text-white',
  };
  return colors[severity] || 'bg-gray-500 text-white';
}

export function getTypeLabel(type: NCRType): string {
  const labels: Record<NCRType, string> = {
    internal: 'Interna',
    supplier: 'Proveedor',
    customer: 'Cliente',
  };
  return labels[type] || type;
}

export function getNextStatus(currentStatus: NCRStatus): NCRStatus | null {
  const flow: Partial<Record<NCRStatus, NCRStatus>> = {
    open: 'investigation',
    investigation: 'corrective_action',
    corrective_action: 'verification',
    verification: 'closed',
  };
  return flow[currentStatus] || null;
}

export function getActionLabel(action: string): string {
  const labels: Record<string, string> = {
    created: 'NCR creada',
    status_change: 'Cambio de estado',
    comment: 'Comentario',
    assignment: 'Asignación',
    attachment: 'Adjunto agregado',
  };
  return labels[action] || action;
}
